import { useApplicationConfigStore } from '~/store/application-config'
import { useBorrowerStore } from '~/store/borrower'
import { useRootStore } from '~/store/root'
import { useIdentityStore } from '~/store/identity'
import { ApplicationConfigService } from '~/libs/store-services/application-config-service'

function loadAnswersFromParams(query) {
    const applicationConfigService = new ApplicationConfigService() // Loading without $axios; Only need localStorage

    const answers = {
      'financeAmount': query.amountSeeking || query.financeAmount
    }
    const validators = {
      'financeAmount': /^\d+$/
    }

    for (const key in answers) {
      let answerEmpty = answers[key] === null || answers[key] === undefined
      if (answerEmpty) {
        delete answers[key]
      }

      if (!answerEmpty && validators[key] && !validators[key].test(answers[key])) {
        console.warn('Not saving data from params because it doesn\'t match validation.', { [key]: answers[key] })
        delete answers[key]
      }
    }

    if (Object.keys(answers).length <= 0) {
      return
    }

    applicationConfigService.mergeAnswersToStorage(answers)
}

export default defineNuxtRouteMiddleware(async (to, from) => {
  setTrackingCookiesFromParams(to.query)
  const { entrypointIdentifier, moduleIdentifier: toModuleIdentifier, internalRouteCore } = getAppConfigRouteData(to)
  loadAnswersFromParams(to.query)
  const { borrower } = useBorrowerStore()
  const { isLoggedIn, authUser, isEmbedded } = useRootStore()
  const appConfigStore = useApplicationConfigStore()
  const { moduleIdentifier: fromModuleIdentifier } = getAppConfigRouteData(from)
  const { mustSetPassword } = useIdentityStore()

  const { currentModule: prevApplicationModule, getApplicationConfigData, setIsBackEvent } = appConfigStore

  await getApplicationConfigData({ entrypointIdentifier, moduleIdentifier: toModuleIdentifier })
  const { applicationConfig, currentModuleIdentifier, currentModule } = appConfigStore

  const missingDataMsg = !applicationConfig ? 'Application Config' : !currentModule ? 'Application Module' : !currentModuleIdentifier ? 'Application Module Identifier' : ''
  if (missingDataMsg) {
    throw createError({
      statusCode: 500,
      statusMessage: `Application Config fetch failed for ${internalRouteCore}/${toModuleIdentifier}. Missing Data: ${missingDataMsg}`,
      fatal: true
    })
  }

  const currentIdentifierPath = `${internalRouteCore}/${currentModuleIdentifier}`

  const isAuthRequired = currentModule.authRequired === true

  if ((!isLoggedIn || !borrower) && isAuthRequired) {
    return redirectToWidget(currentIdentifierPath)
  }

  if (!isEmbedded && isAuthRequired && mustSetPassword) {
    return navigateTo(`/newpass?r=${encodeURIComponent(currentIdentifierPath)}`)
  }

  const { isNonLinearExperimentActive } = useNonLinearExperiment()
  if (isNonLinearExperimentActive() && applicationConfig?.allowNonLinear) {
    const { requirementsBaseRoute } = useApplicationConstants()
    const requirementsPath = entrypointIdentifier
      ? `/${requirementsBaseRoute}/${entrypointIdentifier}`
      : `/${requirementsBaseRoute}`
    return navigateTo({ path: requirementsPath, query: to.query }, { replace: true })
  }

  const backNavIdentifier = prevApplicationModule?.navigationSlugs?.back
  const _isBackEvent = backNavIdentifier === toModuleIdentifier
    || (toModuleIdentifier.includes('basic-info') && from?.path === '/owner-info')

  if (_isBackEvent) {
    setIsBackEvent(_isBackEvent)
  }

  to.meta.layout = isEmbedded ? 'embedded' : 'marketplace'

  if (to.meta.pageTransition && typeof to.meta.pageTransition !== 'boolean') {
    if (_isBackEvent){
      to.meta.pageTransition.name = 'slide-fade-page-back'
    } else {
      to.meta.pageTransition.name = 'slide-fade-page'
    }
  }

  // If no moduleIdentifier in URL or it doesn't match currentModuleIdentifier, navigate to provided currentModuleIdentifier
  if (!toModuleIdentifier || toModuleIdentifier !== currentModuleIdentifier) {
    console.log('Missing or mismatched moduleIdentifier, resuming application experience', { moduleIdentifier: currentModuleIdentifier })
    return navigateTo({ path: `${isEmbedded ? '/embedded' : ''}${currentIdentifierPath}`, query: to.query }, { replace: true })
  }
})
